import { ClientApi } from '@quadminds/client-apis/base';
import { axiosResolver } from '@quadminds/shared/utils';
import { MAIL_API_PATHS } from './api-paths';
import { MAIL_API_TOKEN_NAME, DefaultMailApiConfig } from './constants';
import { MailApiConfig } from './interfaces';
import { SendEmailPayload, SendEmailResponse } from './types';

/**
 * Client side Mail API.
 * Handles email sending through cloud functions using SendGrid templates.
 */
export class MailApi extends ClientApi {
    private readonly defaultTemplateId?: string;

    constructor(config: MailApiConfig = DefaultMailApiConfig) {
        super({
            namespace: MAIL_API_TOKEN_NAME,
            ...config,
        });

        this.defaultTemplateId = config.defaultTemplateId;
    }

    /**
     * Send email using SendGrid template
     * @param payload - Email payload with recipient, data, and template information
     * @returns Promise with send email response
     */
    public async sendEmail(payload: SendEmailPayload): Promise<SendEmailResponse> {
        return axiosResolver<SendEmailResponse>(() =>
            this.httpClient.post(MAIL_API_PATHS.SEND_EMAIL, payload, {
                headers: { ...this.getAuthHeaders() },
            }),
        );
    }

    /**
     * Send email with default template (if configured)
     * @param to - Recipient email address
     * @param data - Template data
     * @param templateId - Optional template ID (uses default if not provided)
     * @returns Promise with send email response
     */
    public async sendTemplateEmail(
        to: string,
        data: Record<string, unknown>,
        templateId?: string,
    ): Promise<SendEmailResponse> {
        const templateToUse = templateId || this.defaultTemplateId;

        if (!templateToUse) {
            throw new Error('Template ID is required. Provide templateId parameter or configure defaultTemplateId.');
        }

        return this.sendEmail({
            to,
            data,
            templateId: templateToUse,
        });
    }

    /**
     * Send feedback email (convenience method for mindy-app usage)
     * @param feedbackEmail - Email address to send feedback to
     * @param feedbackData - Feedback data
     * @param templateId - SendGrid template ID
     * @returns Promise with send email response
     */
    public async sendFeedbackEmail(
        feedbackEmail: string,
        feedbackData: Record<string, unknown>,
        templateId: string,
    ): Promise<SendEmailResponse> {
        return this.sendEmail({
            to: feedbackEmail,
            data: feedbackData,
            templateId,
        });
    }

    // Utility Methods
    private getAuthHeaders() {
        const token = this.getCurrentToken();
        return token ? { Authorization: `Bearer ${token}` } : {};
    }

    private getCurrentToken(): string | null {
        return localStorage.getItem('token');
    }
}
